/**
 * knowledgeStoreEngine.js — Rê Leve
 *
 * Base de conhecimento editável da Léve. Persiste em localStorage no
 * frontend (em produção, será sincronizado com Postgres via backend).
 *
 * Estruturas:
 *   - RAG entries: trechos textuais que a Léve consulta antes de responder
 *   - Guardrails: regras que vencem o LLM (códigos G01..G99)
 *   - Contexto: variáveis de negócio (horário de entrega, bairros, etc.)
 *   - Aprendizados: respostas aprovadas que viraram referência
 *
 * Toda alteração gera um event-log para auditoria.
 */

import { publish, EVT } from './liveSyncEngine';

const LS_KEYS = {
  rag:           'releve.knowledge.rag',
  guardrails:    'releve.knowledge.guardrails',
  context:       'releve.knowledge.context',
  learned:       'releve.knowledge.learned',
  log:           'releve.knowledge.log',
};

// ── Seeds iniciais (carregados na primeira execução) ──────────────────────
const SEED_RAG = [
  {
    id: 'rag-1', topic: 'horário de entrega',
    text: 'A Rê Leve entrega de segunda a sexta entre 11h e 11h30 nos bairros: Boa Viagem, Pina, Espinheiro, Madalena, Casa Forte, Tamarineira, Aflitos, Graças, Torre, Cordeiro.',
    tags: ['entrega', 'logística', 'bairros'],
    source: 'seed', createdAt: '2026-05-01', priority: 'alta',
  },
  {
    id: 'rag-2', topic: 'combos premium',
    text: 'Combo Premium: 30 marmitas/mês + bolsa térmica grátis + 15% desconto vs avulso. Pagamento em até 3x sem juros no cartão ou Pix.',
    tags: ['comercial', 'combos', 'preço'],
    source: 'seed', createdAt: '2026-05-01', priority: 'alta',
  },
  {
    id: 'rag-3', topic: 'restrições alimentares',
    text: 'Linhas disponíveis: Sem Glúten, Sem Lactose, Vegetariano, Low Carb, Equilíbrio (macros calculados). Alergias graves (amendoim/frutos do mar) exigem aviso prévio na confirmação do pedido.',
    tags: ['saúde', 'restrição'],
    source: 'seed', createdAt: '2026-05-01', priority: 'alta',
  },
  {
    id: 'rag-4', topic: 'forma de pagamento',
    text: 'Aceita Pix (com confirmação automática), cartão crédito/débito via maquininha na entrega, e em breve Stripe Checkout para pagamento dentro do app.',
    tags: ['comercial', 'pagamento'],
    source: 'seed', createdAt: '2026-05-01', priority: 'média',
  },
];

const SEED_GUARDRAILS = [
  { id: 'G01', name: 'Não prescrever dieta', rule: 'Bot nunca diz "você deve comer X kcal por dia" nem cria plano alimentar personalizado. Encaminha para nutricionista parceira.', severity: 'crítica', active: true },
  { id: 'G02', name: 'Não prometer emagrecimento', rule: 'Bot nunca afirma "vai emagrecer com nossas marmitas". Pode dizer "comida leve apoia uma rotina saudável".', severity: 'crítica', active: true },
  { id: 'G03', name: 'Não substituir profissional', rule: 'Diante de dúvidas clínicas (intolerância grave, diabetes, etc), bot informa que não substitui nutricionista e encaminha contato.', severity: 'crítica', active: true },
  { id: 'G04', name: 'Não expor outros clientes', rule: 'Nunca mencionar nomes ou dados de outros clientes em respostas. Mesmo se cliente pedir, bot recusa.', severity: 'crítica', active: true },
  { id: 'G05', name: 'Não inventar política', rule: 'Bot nunca cria horário, prazo, preço ou condição que não está no RAG. Diante de pergunta sem dado, encaminha para equipe ("Vou perguntar pra equipe").', severity: 'alta', active: true },
  { id: 'G06', name: 'Não criar desconto', rule: 'Descontos só dentro de promoções ativas no RAG. Cliente pedindo desconto fora disso, bot recusa educadamente.', severity: 'alta', active: true },
  { id: 'G07', name: 'Não prometer entrega que não exista', rule: 'Se cliente pede entrega em bairro fora da lista, bot avisa que ainda não atendemos e oferece retirada (se aplicável).', severity: 'alta', active: true },
  { id: 'G08', name: 'Não processar cartão no front', rule: 'Dados de cartão NUNCA vão pelo chat ou pelo frontend. Apenas via link Stripe Checkout (PCI-DSS).', severity: 'crítica', active: true },
  { id: 'G09', name: 'Acompanhamento calórico requer opt-in', rule: 'Para calcular/registrar calorias do cliente, bot precisa consentimento explícito previamente confirmado.', severity: 'alta', active: true },
  { id: 'G10', name: 'Tom da Léve', rule: 'Bot mantém tom recifense jovem: "vixe", "massa", "manda ver", "tá danado". Nunca formal demais.', severity: 'média', active: true },
];

const SEED_CONTEXT = [
  { id: 'ctx-1',  key: 'cidade',                value: 'Recife (PE)', editable: false },
  { id: 'ctx-2',  key: 'região atendida',        value: 'Recife, Olinda, Jaboatão (parcial)', editable: true },
  { id: 'ctx-3',  key: 'horário de entrega',     value: 'Segunda a sexta, 11h00–11h30', editable: true },
  { id: 'ctx-4',  key: 'horário de atendimento', value: 'Segunda a sábado, 08h00–18h00', editable: true },
  { id: 'ctx-5',  key: 'WhatsApp da equipe',     value: '(a definir)', editable: true },
  { id: 'ctx-6',  key: 'prazo de cancelamento',  value: 'Até 18h do dia anterior à entrega', editable: true },
  { id: 'ctx-7',  key: 'política de reembolso',  value: 'Reembolso integral via Pix em 2 dias úteis', editable: true },
  { id: 'ctx-8',  key: 'fornecedores principais', value: 'Açougue Boa Viagem · Hortifruti Pina · Mundo Verde · Granja Pina · Embalax · Mercadão', editable: true },
];

const SEED_LEARNED = []; // Aprendizados são acumulados via aprovações

// ── Helpers de persistência ────────────────────────────────────────────────
function load(key, seed) {
  if (typeof window === 'undefined') return seed;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      localStorage.setItem(key, JSON.stringify(seed));
      return seed;
    }
    return JSON.parse(raw);
  } catch {
    return seed;
  }
}

function save(key, data) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
}

function logEvent(type, payload) {
  if (typeof window === 'undefined') return;
  try {
    const log = JSON.parse(localStorage.getItem(LS_KEYS.log) || '[]');
    log.unshift({ ts: new Date().toISOString(), type, payload });
    // Mantém só os últimos 200 eventos
    localStorage.setItem(LS_KEYS.log, JSON.stringify(log.slice(0, 200)));
  } catch {}
}

// ── RAG (Retrieval-Augmented Generation) ───────────────────────────────────
export function getRAG()             { return load(LS_KEYS.rag, SEED_RAG); }
export function addRAG(entry) {
  const list = getRAG();
  const id = entry.id || ('rag-' + (list.length + 1));
  const newE = { ...entry, id, createdAt: new Date().toISOString().slice(0, 10), source: entry.source || 'manual' };
  const next = [newE, ...list];
  save(LS_KEYS.rag, next);
  logEvent('rag.add', { id, topic: entry.topic });
  publish(EVT.KNOWLEDGE_RAG_UPDATED, { action: 'add', id, topic: entry.topic });
  return newE;
}
export function updateRAG(id, patch) {
  const list = getRAG();
  const next = list.map(e => e.id === id ? { ...e, ...patch, updatedAt: new Date().toISOString().slice(0, 10) } : e);
  save(LS_KEYS.rag, next);
  logEvent('rag.update', { id, fields: Object.keys(patch) });
  publish(EVT.KNOWLEDGE_RAG_UPDATED, { action: 'update', id, fields: Object.keys(patch) });
  return next.find(e => e.id === id);
}
export function deleteRAG(id) {
  const list = getRAG();
  const next = list.filter(e => e.id !== id);
  save(LS_KEYS.rag, next);
  logEvent('rag.delete', { id });
  publish(EVT.KNOWLEDGE_RAG_UPDATED, { action: 'delete', id });
}

// ── Guardrails ─────────────────────────────────────────────────────────────
export function getGuardrails()      { return load(LS_KEYS.guardrails, SEED_GUARDRAILS); }
export function updateGuardrail(id, patch) {
  const list = getGuardrails();
  const next = list.map(g => g.id === id ? { ...g, ...patch } : g);
  save(LS_KEYS.guardrails, next);
  logEvent('guardrail.update', { id, fields: Object.keys(patch) });
  publish(EVT.KNOWLEDGE_GUARDRAIL_TOGGLED, { id, patch });
  return next.find(g => g.id === id);
}
export function addGuardrail(g) {
  const list = getGuardrails();
  const id = g.id || ('G' + String(list.length + 1).padStart(2, '0'));
  const newG = { ...g, id, active: g.active ?? true };
  const next = [...list, newG];
  save(LS_KEYS.guardrails, next);
  logEvent('guardrail.add', { id });
  return newG;
}

// ── Contexto ───────────────────────────────────────────────────────────────
export function getContext()         { return load(LS_KEYS.context, SEED_CONTEXT); }
export function updateContext(id, value) {
  const list = getContext();
  const next = list.map(c => c.id === id ? { ...c, value, updatedAt: new Date().toISOString() } : c);
  save(LS_KEYS.context, next);
  logEvent('context.update', { id, value });
  publish(EVT.KNOWLEDGE_CONTEXT_UPDATED, { id, value });
  return next.find(c => c.id === id);
}
export function addContext({ key, value }) {
  const list = getContext();
  const id = 'ctx-' + Date.now();
  const newC = { id, key, value, editable: true, createdAt: new Date().toISOString() };
  const next = [...list, newC];
  save(LS_KEYS.context, next);
  logEvent('context.add', { id, key });
  publish(EVT.KNOWLEDGE_CONTEXT_UPDATED, { action: 'add', id, key, value });
  return newC;
}
export function deleteContext(id) {
  const list = getContext();
  save(LS_KEYS.context, list.filter(c => c.id !== id));
  logEvent('context.delete', { id });
  publish(EVT.KNOWLEDGE_CONTEXT_UPDATED, { action: 'delete', id });
}

// ── Aprendizado manual (não vindo de aprovação) ──────────────────────────
export function addManualLearned({ question, answer, tags = [] }) {
  return addLearned({
    fromQuestion: question,
    answer,
    tags,
    customerSegment: 'manual',
    answeredAt: new Date().toISOString(),
  });
}

// ── Aprendizados (vindos de aprovações da equipe) ──────────────────────────
export function getLearned()         { return load(LS_KEYS.learned, SEED_LEARNED); }
export function addLearned(entry) {
  const list = getLearned();
  const id = entry.id || ('learn-' + Date.now());
  const newE = { ...entry, id, createdAt: new Date().toISOString() };
  const next = [newE, ...list];
  save(LS_KEYS.learned, next);
  logEvent('learned.add', { id, fromQuestion: entry.fromQuestion });
  return newE;
}
export function deleteLearned(id) {
  const list = getLearned();
  save(LS_KEYS.learned, list.filter(e => e.id !== id));
  logEvent('learned.delete', { id });
}

// ── Log de eventos ─────────────────────────────────────────────────────────
export function getLog({ limit = 50 } = {}) {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(LS_KEYS.log) || '[]').slice(0, limit); }
  catch { return []; }
}

// ── Export bulk (download JSON da base inteira) ────────────────────────────
export function exportAll() {
  return {
    exportedAt: new Date().toISOString(),
    rag: getRAG(),
    guardrails: getGuardrails(),
    context: getContext(),
    learned: getLearned(),
  };
}

// ── Busca textual simples no RAG (para o bot) ──────────────────────────────
export function searchRAG(query, { limit = 3 } = {}) {
  if (!query) return [];
  const q = query.toLowerCase();
  const rag = getRAG();
  const scored = rag.map(e => {
    let score = 0;
    if (e.topic && e.topic.toLowerCase().includes(q)) score += 5;
    if (e.tags && e.tags.some(t => q.includes(t.toLowerCase()))) score += 3;
    if (e.text && e.text.toLowerCase().includes(q)) score += 2;
    if (e.priority === 'alta') score += 1;
    return { entry: e, score };
  });
  return scored
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(s => s.entry);
}
